import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { confirmSignIn } from 'aws-amplify/auth';
import AuthShell from '../../components/auth/AuthShell.tsx';
import { authConfig } from '../../config/auth-config.ts';
import { humanizeAuthError } from '../../auth/errors.ts';
import { DEFAULT_POST_AUTH, isSafeNext } from '../../auth/postAuthRedirect.ts';
import { useAuth } from '../../auth/useAuth.ts';
import './CheckEmail.css';
import { requestTimeout } from '../../config/requestTimeout.ts';
import { recordClientFailure } from '../../telemetry/rum.ts';

interface CheckEmailNavState {
  readonly token?: string;
  /** Post-approval redirect target threaded from sign-in/sign-up. */
  readonly next?: string;
}

type Phase = 'polling' | 'completing' | 'completed' | 'expired' | 'error';

const POLL_INTERVAL_MS = 2000;
// Five minutes — matches the DDB row TTL in CreateAuthChallenge.
const POLL_LIMIT_MS = 5 * 60 * 1000;

function readToken(state: unknown): string | undefined {
  if (state && typeof state === 'object') {
    const s = state as CheckEmailNavState;
    if (typeof s.token === 'string' && /^[0-9a-f-]{36}$/i.test(s.token)) {
      return s.token;
    }
  }
  return undefined;
}

/** Validated post-approval redirect target, or the /app default. */
function readNextTarget(state: unknown): string {
  if (state && typeof state === 'object') {
    const s = state as CheckEmailNavState;
    if (isSafeNext(s.next)) {
      return s.next;
    }
  }
  return DEFAULT_POST_AUTH;
}

export default function CheckEmail() {
  const location = useLocation();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const token = readToken(location.state);
  const nextTarget = readNextTarget(location.state);

  const [phase, setPhase] = useState<Phase>('polling');
  const [error, setError] = useState<string | null>(null);
  // Guard ref — flipped to true the instant we commit to completing,
  // so concurrent poll ticks can't both call confirmSignIn() on the
  // same approval row (the second one fails the conditional delete
  // and shows the user a transient error).
  const startedCompletingRef = useRef(false);
  // Report a poll failure at most ONCE per mount. The tick fires every 2s, so
  // recording every failure would turn one stuck browser into ~30 telemetry
  // events a minute; one event is all it takes to make the failure findable.
  const reportedPollFailureRef = useRef(false);

  useEffect(() => {
    if (token === undefined) {
      return;
    }
    const startedAt = Date.now();
    // Read through a function: the cleanup mutates this across closures,
    // which TS's narrowing can't see — a call returns honest `boolean`.
    const cancelled = { current: false };
    const isCancelled = (): boolean => cancelled.current;
    const startedCompleting = (): boolean => startedCompletingRef.current;
    let intervalId: number | undefined;

    const stopPolling = (): void => {
      if (intervalId !== undefined) {
        window.clearInterval(intervalId);
        intervalId = undefined;
      }
    };

    const complete = async (): Promise<void> => {
      if (isCancelled() || startedCompleting()) {
        return;
      }
      // Atomic: claim the completion slot before any awaits so a second
      // tick fired concurrently sees `startedCompletingRef.current === true`
      // and returns early.
      startedCompletingRef.current = true;
      stopPolling();
      setPhase('completing');
      try {
        const out = await confirmSignIn({ challengeResponse: token });
        if (isCancelled()) {return;}
        if (out.isSignedIn) {
          // Amplify's session cache can lag confirmSignIn by a tick — refresh()
          // right after can transiently land 'unauthenticated', which bounced
          // the user to home instead of the signed-in app (user report
          // 2026-07-07). Retry until auth state settles before navigating.
          let authed = false;
          for (let attempt = 0; attempt < 6 && !authed; attempt++) {
            authed = await refresh();
            if (isCancelled()) {return;}
            if (!authed) {
              await new Promise((resolve) => setTimeout(resolve, 250));
            }
          }
          setPhase('completed');
          navigate(nextTarget, { replace: true });
        } else {
          setError(
            `Unexpected sign-in step after approval: ${out.nextStep.signInStep}. Sign in again.`,
          );
          setPhase('error');
        }
      } catch (err) {
        if (isCancelled()) {return;}
        setError(humanizeAuthError(err));
        setPhase('error');
      }
    };

    const tick = async (): Promise<void> => {
      if (isCancelled() || startedCompleting()) {
        return;
      }
      if (Date.now() - startedAt > POLL_LIMIT_MS) {
        stopPolling();
        setPhase('expired');
        return;
      }
      try {
        const res = await fetch(
          `${authConfig.authStatusUrl}?token=${encodeURIComponent(token)}`,
          { method: 'GET', signal: requestTimeout() },
  );
        if (isCancelled() || startedCompleting()) {return;}
        if (!res.ok) {
          // 4xx is permanent (malformed token, unknown row) — stop polling.
          if (res.status >= 400 && res.status < 500) {
            stopPolling();
            setPhase('expired');
            return;
          }
          // 5xx is transient; retry on next tick.
          return;
        }
        const body = (await res.json()) as { status?: string };
        if (body.status === 'approved') {
          await complete();
        } else if (body.status === 'expired') {
          stopPolling();
          setPhase('expired');
        }
        // 'pending' → keep polling
      } catch (err) {
        // A single failed tick is a network blip: keep polling, the next tick
        // usually succeeds. But it must not be SILENT.
        //
        // The old comment here claimed "CloudWatch on the Lambda side captures
        // the request failures". That was wrong, and the way it was wrong cost
        // a prod sign-in on 2026-08-22: when the throw happens BEFORE `fetch`
        // (it was `AbortSignal.timeout`, absent on Safari < 16), no request is
        // ever issued, so there is nothing for the Lambda to log, nothing for
        // RUM's http telemetry to see, and — because this catch swallowed it —
        // no uncaught error either. The page polled zero times and waited
        // forever while the API Gateway log showed a clean, approved sign-in.
        //
        // One event, once per mount, is what makes that state findable.
        if (!reportedPollFailureRef.current) {
          reportedPollFailureRef.current = true;
          recordClientFailure(
            'auth_status_poll_failed',
            err instanceof Error ? `${err.name}: ${err.message}` : String(err),
          );
        }
      }
    };

    void tick();
    intervalId = window.setInterval(() => {
      void tick();
    }, POLL_INTERVAL_MS);

    return () => {
      cancelled.current = true;
      stopPolling();
    };
  }, [token, navigate, refresh, nextTarget]);

  if (token === undefined) {
    return <Navigate to="/auth/signin" replace />;
  }

  if (phase === 'expired') {
    return (
      <AuthShell
        title="Approval link expired"
        subtitle="The approval link is only valid for 5 minutes. Sign in again to get a fresh one."
      >
        <Link to="/auth/signin" className="om-check-email__cta">
          Sign in again
        </Link>
      </AuthShell>
    );
  }

  if (phase === 'error') {
    return (
      <AuthShell
        title="Sign-in failed"
        subtitle={error ?? 'Something went wrong finishing your sign-in.'}
      >
        <Link to="/auth/signin" className="om-check-email__cta">
          Try again
        </Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Check your email"
      subtitle={
        phase === 'completing'
          ? 'Approved — finishing sign-in…'
          : 'We sent a link. Click it on any device — the sign-in completes here automatically.'
      }
    >
      <div className="om-check-email__waiting" role="status" aria-live="polite">
        <span className="om-check-email__pulse" aria-hidden="true" />
        <span className="om-check-email__waiting-label">
          {phase === 'completing' ? 'Signing you in' : 'Waiting for approval'}
        </span>
      </div>
      {phase === 'polling' ? (
        <p className="om-check-email__spam" role="note">
          <strong>Not in your inbox?</strong> Check your <strong>Spam</strong> or{' '}
          <strong>Junk</strong> folder — approval emails land there for some
          providers. Marking it as not spam keeps the next one out of the filter.
        </p>
      ) : null}
      <p className="om-check-email__hint">
        Wrong account?{' '}
        <Link to="/auth/signin" className="om-check-email__link">
          Sign in again
        </Link>
      </p>
    </AuthShell>
  );
}
